import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Banner,
  Card,
  CardContent,
  Typography,
} from '@forgeglobal/design-framework';

import Spinner from '@tcs/common/components/atoms/Spinner';
import useAccountApplication from '@tcs/client-portal-common/hooks/useAccountApplication';
import { useStyles } from './style';

interface AccountApplication {
  FullName?: string;
  Email?: string;
  DateOfBirth?: string;
  AccountType?: string;
  SsnLastFour?: string;
  CreatedAt?: string;
}

const formatDate = (value?: string) => {
  if (!value) return 'N/A';

  // DOB should be treated as a calendar date, not a moment in local time.
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T|\s)/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${month}/${day}/${year}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(parsed);
};

const formatDateTime = (value?: string) => {
  if (!value) return 'N/A';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const formatSsnLastFour = (value?: string) => {
  if (!value) return 'N/A';
  return `***-**-${value}`;
};

const Application = () => {
  const classes = useStyles();
  const { applicationId } = useParams();
  const { state } = useLocation() as { state?: { searchValue?: string } };
  const { data, loading } = useAccountApplication(applicationId);
  const application = (data ?? {}) as AccountApplication;
  const searchValue = state?.searchValue ?? '';

  if (loading) {
    return <Spinner />;
  }

  if (!data) {
    return (
      <Typography
        variant="body1"
        color="textSecondary"
        className={classes.pageContainer}>
        No application details were found.
      </Typography>
    );
  }

  return (
    <div className={classes.pageContainer}>
      <div className={classes.sectionSpacing}>
        <Typography variant="h5" color="textPrimary">
          Search results for &quot;{searchValue}&quot;
        </Typography>
      </div>
      <div className={classes.sectionSpacing}>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <div className={classes.titleRow}>
              <Typography
                variant="h6"
                color="textPrimary"
                className={classes.title}>
                {application.FullName ?? 'N/A'}
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                className={classes.email}>
                {application.Email ?? 'N/A'}
              </Typography>
            </div>

            <div className={classes.bannerWrapper}>
              <Banner severity="warning">
                <Typography variant="body2" className={classes.bannerText}>
                  This application has been submitted but has not yet been
                  ingested into MPE. It will remain in Pending status until
                  processing is complete.
                </Typography>
              </Banner>
            </div>

            <div className={classes.grid}>
              <div className={classes.gridCell}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.label}>
                  NAME
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className={classes.value}>
                  {application.FullName ?? 'N/A'}
                </Typography>
              </div>

              <div className={classes.gridCell}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.label}>
                  EMAIL ADDRESS
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className={classes.value}>
                  {application.Email ?? 'N/A'}
                </Typography>
              </div>

              <div className={classes.gridCell}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.label}>
                  DATE & TIME OF SUBMISSION
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className={classes.value}>
                  {formatDateTime(application.CreatedAt)}
                </Typography>
              </div>

              <div className={classes.gridCell}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.label}>
                  ACCOUNT TYPE
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className={classes.value}>
                  {application.AccountType ?? 'N/A'}
                </Typography>
              </div>

              <div className={classes.gridCell}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.label}>
                  DATE OF BIRTH
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className={classes.value}>
                  {formatDate(application.DateOfBirth)}
                </Typography>
              </div>

              <div className={classes.gridCell}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.label}>
                  LAST FOUR OF SSN
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className={classes.value}>
                  {formatSsnLastFour(application.SsnLastFour)}
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Application;
